import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { QbItaStep, getDate } from '@fwmovilidad/qb-home-ita-components-lib';
import { combineLatest, Observable, Subject } from 'rxjs';
import { Flow, RoutePaths } from '@global/routePaths';
import { Coupon } from '@core/models/swagger/coupon';
import { getCoupon } from '@store/flow-data/coupon';
import { select, Store } from '@ngrx/store';
import { AppState } from '@store/app.state';
import { FOOTER_LINKS, VERTI_URL } from '@global/constants';
import { FooterLink } from '@core/models/swagger/footerLink';
import { getLastLevelPath } from '@global/utils';
import { InitFlow } from '@core/models/swagger/initFlow';
import { getInitFlow } from '@store/flow-data/init-flow';
import { first, map, switchMap, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ExitIntentService } from '@core/services/exit-modal/exit-intent-modal-service';
import {
  getDeductibleOptions,
  getPaymentMethods,
  getSelectedVehicle,
  getPossibleVehicles,
  getRecommendations,
  getVehicleSelection,
  getCarInsurers,
} from '@store/flow-data/general';
import cloneDeep from 'clone-deep';
import { SectionIdSummaryLite, SubSectionIdSummaryLite, SummaryLiteData } from '@core/models/swagger/summaryLiteData';
import { SummaryHelperService } from '@core/services/summary/summaryHelper.service';

@Component({
  selector: 'qb-de-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  standalone: false,
})
export class LayoutComponent implements OnInit, OnDestroy {
  protected destroy$: Subject<boolean> = new Subject<boolean>();
  footerYear = getDate().getFullYear();
  FOOTER_LINKS: readonly FooterLink[] = FOOTER_LINKS;
  footerLinks: FooterLink[];
  WELCOME_PAGE_NAME: string = getLastLevelPath(RoutePaths.NEW_QUOTATION.WELCOME.route);
  VERTI_URL: string = VERTI_URL;
  initFlowData: InitFlow;

  @Input() contentCentered = false;
  @Input() contentPadding = false;

  // Page spinner
  @Input() lockUserInteraction: boolean;

  // Content component info
  @Input() stepPageTitle?: string;
  @Input() stepPageDescription?: string;
  @Input() stepPageIcon?: string;

  // Summary lite
  @Input() showSummaryLite = false;
  @Input() showSummaryLiteCoverages: boolean = false;
  @Input() showSummaryLitePDF: boolean = false;
  @Input() summaryLiteSectionExpandedId: SectionIdSummaryLite;
  @Input() summaryLiteSubSectionExpandedId: SubSectionIdSummaryLite;
  @Output() summaryLitePDFClick: EventEmitter<string> = new EventEmitter();

  // Header inputs
  @Input() headerPromotionalImage?: string;
  @Input() headerPromotionalUrl?: string;
  @Input() headerPromotionalShowInd = true;
  @Input() hideHeaderTitleInd?: boolean;

  // Footer inputs
  @Input() footerCookiesUrl?: string;
  @Input() footerPrivacyUrl?: string;
  @Input() footerDocumentUrl?: string;
  // Steps inputs
  @Input() steps: QbItaStep[];
  @Input() showSteps = true;
  /**
   * Decimal format. First number is the main step, second number is substep
   * @example 1.1
   */
  @Input() currentStep: string;
  @Input() percent?: number;
  @Output() stepClicked: EventEmitter<string> = new EventEmitter();

  // Back button
  @Input() hideBackButton = false;
  @Input() backBtnTitle?: string;
  @Input() backBtnDescription?: string;
  @Input() showNewEstimateButton = false;
  @Output() backBtnClick: EventEmitter<void> = new EventEmitter();

  // Continue button
  @Input() hideNextButton = false;
  @Input() hideNextButtonMobile = false;
  @Input() nextBtnTitle?: string;
  @Input() nextBtnDisabled?: boolean;
  @Input() nextBtnPosition?: string;
  @Input() nextButtonShowSpinner?: boolean;
  @Output() nextBtnClick: EventEmitter<void> = new EventEmitter();

  // Secondary button
  @Input() hideSecondaryButton = true;
  @Input() hideSecondaryButtonMobile = false;
  @Input() secondaryBtnTitle?: string;
  @Input() secondaryBtnDisabled?: boolean;
  @Input() secondaryButtonShowSpinner?: boolean;
  @Output() secondaryBtnClick: EventEmitter<void> = new EventEmitter();

  @Input() flowName: Flow;
  @Input() stepName: string;
  @Input() showSeoFooter: boolean;

  @Input() pageLoading: boolean;

  onDesktop: boolean;

  subtitle: string;

  coupon: Coupon;

  getLastLevelPath = getLastLevelPath;

  RoutePaths = RoutePaths;

  Flow = Flow;

  summaryLiteData$: Observable<SummaryLiteData | undefined>;

  phoneNumber: string;

  isSummaryExpanded: boolean = false;

  constructor(
    private readonly store: Store<AppState>,
    private readonly translateService: TranslateService,
    private readonly exitIntentService: ExitIntentService,
    private readonly summaryHelperService: SummaryHelperService
  ) {}

  ngOnInit(): void {
    this.onDesktop = window.innerWidth > 768;

    this.footerLinks = FOOTER_LINKS.map(link => {
      if (link.type === 'copyright') {
        return {
          ...link,
          label: this.translateService.instant('FOOTER.COPYRIGHT.TEXT', { year: getDate().getFullYear() }),
        };
      }
      return link;
    });

    this.store.pipe(select(getInitFlow), takeUntil(this.destroy$)).subscribe(initFlowData => {
      this.initFlowData = initFlowData;

      this.phoneNumber = this.initFlowData?.campaignInfo?.telephoneNumber ?? this.phoneNumber;
    });

    this.store
      .pipe(select(getCoupon))
      .pipe(takeUntil(this.destroy$))
      .subscribe((coupon: Coupon) => {
        this.coupon = coupon;
      });

    if (this.showSummaryLite) {
      this.summaryLiteData$ = this.store.select(getVehicleSelection).pipe(
        first(),
        switchMap(vehicleSelection =>
          combineLatest([
            this.store.select(getDeductibleOptions),
            this.store.select(getPaymentMethods),
            this.store.select(getSelectedVehicle).pipe(first()),
            this.store.select(getPossibleVehicles).pipe(first()),
            this.store.select(getRecommendations),
            this.summaryHelperService.getSummaryVehicleInformationString(vehicleSelection, true),
            this.store.select(getCarInsurers).pipe(first()),
          ]).pipe(
            map(
              ([
                deductibleOption,
                paymentMethods,
                selectedVehicleIndex,
                possibleVehicles,
                recommendations,
                vehicleSummaryInfo,
                carInsurers,
              ]) => {
                const selectedVehicle = possibleVehicles[selectedVehicleIndex];
                return this.summaryHelperService.buildSummaryLiteData(
                  {
                    deductibleOption,
                    paymentMethods,
                    selectedVehicle,
                    recommendations,
                    vehicleSummaryInfo,
                    carInsurers,
                  },
                  this.coupon,
                  this.showSummaryLiteCoverages,
                  this.summaryLiteSectionExpandedId,
                  this.summaryLiteSubSectionExpandedId
                );
              }
            )
          )
        ),
        takeUntil(this.destroy$)
      );
    }

    if (this.onDesktop && !sessionStorage.getItem('exit-intent-shown')) {
      this.setupExitIntentTriggers();
    }
  }

  private setupExitIntentTriggers(): void {
    let inactivityTimer: ReturnType<typeof setTimeout>;
    const resetTimer = (): void => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => this.triggerExitIntent(), 90000);
    };

    ['mousemove', 'scroll', 'click'].forEach(event => window.addEventListener(event, resetTimer));
    resetTimer();

    document.addEventListener('mouseleave', (e: MouseEvent) => {
      if (e.clientY <= 0) {
        this.triggerExitIntent();
      }
    });
  }

  private triggerExitIntent(): void {
    this.exitIntentService.showModal();
  }

  replaceHyphensWithSpaces(text: string): string {
    return text.replace(/-/g, ' ');
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  goToVerti(): void {
    window.location.href = 'https://www.verti.de/';
  }

  onHeaderPhoneNumberClick(): void {
    const rawVertiPhone = this.phoneNumber;

    if (!rawVertiPhone) {
      return;
    }

    window.location.href = `tel:${rawVertiPhone.replace(/[^\d+]/g, '')}`;
  }

  protected cloneDeep<T>(value: T): T {
    // return window.structuredClone(value);
    return cloneDeep(value);
  }

  downloadPDF(): void {}
}
